import { environment } from "@app/relay/relay-environment";
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import type { UppyFile, ErrorResponse } from "@uppy/core";
import { DragDrop } from "@uppy/react";
import { useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { walletUploader_PresignQuery } from "@app/queries/walletUploader_PresignQuery.graphql";
import type { walletUploader_StartMutation } from "@app/queries/walletUploader_StartMutation.graphql";

import "@uppy/core/dist/style.min.css";
import "@uppy/drag-drop/dist/style.min.css";

const presignQuery = graphql`
  query walletUploader_PresignQuery($fileExtension: String!, $nonce: String!) {
    uploadPresign(fileExtension: $fileExtension, nonce: $nonce) {
      signedUrl
      storageKey
    }
  }
`;

const startMutation = graphql`
  mutation walletUploader_StartMutation($slug: String!, $input: MultiUploadInputObject!) {
    walletUploadStart(slug: $slug, multiUploadInput: $input) {
      job {
        status
        phase
      }
      errors {
        message
      }
    }
  }
`;

type UploadedDescriptor = {
  id: string;
  storage: "cache";
  metadata: { size: number; filename: string; mimeType: string };
};

// Multi-file CSV uploader for a wallet slug: files stream to S3 via presign
// (Uppy + AwsS3), then — once every file is in S3 — walletUploadStart opens a
// promotion run and onStarted fires so the page's progress bar takes over the
// server-side steps. Mirrors the proven /statements UploadZone S3 setup
// (including the StrictMode-safe instance creation).
export default function WalletUploader({
  slug,
  onStarted,
  hint = "Drop the Deel/Wise CSV exports here — you can select several at once.",
}: {
  slug: string;
  onStarted: () => void;
  hint?: string;
}) {
  const uploadedRef = useRef<UploadedDescriptor[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uppy, setUppy] = useState<Uppy | null>(null);

  // Created inside an effect so it survives StrictMode's mount→unmount→remount
  // (the cleanup's uppy.close() uninstalls AwsS3; owning creation here rebuilds
  // the uploader on remount — see UploadZone for the full rationale).
  useEffect(() => {
    const instance = new Uppy({
      restrictions: { allowedFileTypes: [".csv"], maxNumberOfFiles: 50 },
      autoProceed: true,
    }).use(AwsS3, {
      async getUploadParameters(file: UppyFile) {
        const extension = file.name.split(".").pop() ?? "csv";
        const data = await fetchQuery<walletUploader_PresignQuery>(
          environment,
          presignQuery,
          { fileExtension: extension, nonce: file.id },
          { fetchPolicy: "network-only" }
        ).toPromise();

        const presign = data?.uploadPresign;
        if (!presign) throw new Error("Could not presign upload");

        instance.setFileMeta(file.id, { shrineId: presign.storageKey });
        return {
          method: "PUT",
          url: presign.signedUrl,
          fields: {},
          headers: { "Content-Type": file.type ?? "text/csv" },
        };
      },
    });
    setUppy(instance);
    return () => instance.close();
  }, []);

  useEffect(() => {
    if (!uppy) return;

    const onSuccess = (file: UppyFile | undefined) => {
      if (!file) return;
      uploadedRef.current.push({
        id: String(file.meta.shrineId),
        storage: "cache",
        metadata: {
          size: file.size,
          filename: file.name,
          mimeType: file.type ?? "text/csv",
        },
      });
      setUploadedFiles((prev) => [...prev, file.name]);
    };

    // Every file in the batch has reached S3 → open the promotion run.
    //
    // The batch is taken OUT of the ref before it is submitted, not after it
    // succeeds. Leaving it there on failure meant the next drop submitted the old
    // descriptors alongside the new ones — importing files the user had not chosen,
    // as a second batch, each one its own full pipeline run. Losing a failed batch
    // costs a re-drop; keeping it costs an import nobody asked for.
    const onComplete = () => {
      const batch = uploadedRef.current;
      if (batch.length === 0) return;
      uploadedRef.current = [];

      setStarting(true);
      commitMutation<walletUploader_StartMutation>(environment, {
        mutation: startMutation,
        variables: { slug, input: { uploads: batch } },
        onCompleted: (response) => {
          setStarting(false);
          const err = response.walletUploadStart?.errors?.[0]?.message;
          if (err) {
            setError(`${err} Nothing was imported — drop the files again to retry.`);
            setUploadedFiles([]);
            return;
          }
          setUploadedFiles([]);
          onStarted();
        },
        onError: (e) => {
          setStarting(false);
          setUploadedFiles([]);
          setError(`${e.message} Nothing was imported — drop the files again to retry.`);
        },
      });
    };

    const onUploadError = (
      file: UppyFile | undefined,
      err: Error,
      response?: ErrorResponse
    ) => {
      const body = response?.body;
      const detail =
        typeof body === "string" && body.length > 0
          ? body.slice(0, 300)
          : body != null
            ? JSON.stringify(body).slice(0, 300)
            : err.message;
      const status = response?.status ? ` (HTTP ${response.status})` : "";
      setError(`Upload failed for “${file?.name ?? "file"}”${status}: ${detail}`);
    };

    const onRestrictionFailed = (file: UppyFile | undefined, err: Error) =>
      setError(`${file?.name ? `“${file.name}”: ` : ""}${err.message}`);
    const onFileAdded = () => setError(null);

    uppy.on("upload-success", onSuccess);
    uppy.on("complete", onComplete);
    uppy.on("upload-error", onUploadError);
    uppy.on("restriction-failed", onRestrictionFailed);
    uppy.on("file-added", onFileAdded);
    return () => {
      uppy.off("upload-success", onSuccess);
      uppy.off("complete", onComplete);
      uppy.off("upload-error", onUploadError);
      uppy.off("restriction-failed", onRestrictionFailed);
      uppy.off("file-added", onFileAdded);
    };
  }, [uppy, slug, onStarted]);

  return (
    <div className="space-y-3 [&_.uppy-DragDrop-container]:min-h-32">
      {uppy && <DragDrop uppy={uppy} height="128px" note={hint} />}

      {uploadedFiles.length > 0 && (
        <ul className="divide-y rounded border text-sm">
          {uploadedFiles.map((name) => (
            <li key={name} className="flex justify-between px-3 py-1.5">
              <span className="truncate">{name}</span>
              <span className="shrink-0 text-green-600">in S3</span>
            </li>
          ))}
        </ul>
      )}

      {starting && (
        <p className="text-sm text-gray-500">Starting promotion…</p>
      )}

      {error && (
        <div
          role="alert"
          className="break-words rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      )}
    </div>
  );
}
